import type { Client as FilestackClient } from 'filestack-js';
import { EFitOptions } from 'filestack-js';
import type { TFunction } from 'i18next';
import { when } from 'mobx';

import type {
  BaseCampaign,
  Creative,
  DisplayCreative,
  FacebookDisplayCreative,
  IBannersnackUrl,
  ICreativeAuditStatus,
  IDimensionDefinition,
  TCreativeAuditStatus,
} from '@feathr/blackbox';
import {
  CreativeClass,
  getAspectRatioNotation,
  isBannersnackCreative,
  metaAspectRatiosMatch,
  ValidDimensions,
  ValidFacebookDimensions,
  ValidFacebookVideoDimensions,
} from '@feathr/blackbox';
import type { TCreativeStatus } from '@feathr/components';
import { toast } from '@feathr/components';

import type { TChannel } from './types';

import noImg300x250 from '@feathr/extender/images/no-img-300x250.png';

// =============================================================================
// Shared File Processing Utilities
// =============================================================================

/**
 * Video dimension metadata extracted from a video element.
 */
export interface IVideoDimensions {
  width: number;
  height: number;
  duration: number;
}

/**
 * Extracts video dimensions and duration from a video URL.
 *
 * @param url - The URL of the video to analyze
 * @returns Promise resolving to video dimensions and duration
 */
export function getVideoDimensions(url: string): Promise<IVideoDimensions> {
  return new Promise<IVideoDimensions>((resolve) => {
    const video = document.createElement('video');
    const handleLoadedMetadata = (): void => {
      const dimensions = {
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration,
      };
      // Clean up
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.src = '';
      // Forces the media element to reset
      video.load();
      resolve(dimensions);
    };
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.src = url;
  });
}

/**
 * Sanitizes a filename by removing special characters and replacing spaces.
 *
 * @param filename - The original filename to sanitize
 * @returns The sanitized filename safe for use in URLs and storage
 */
export function sanitizeFilename(filename: string): string {
  return filename.replaceAll(/[^a-zA-Z0-9_\-\s.]/g, '').replaceAll(/\s+/g, '_');
}

// =============================================================================
// Filestack Image Transformation Utilities
// =============================================================================

/** Cached Filestack client instance */
let cachedFilestackClient: FilestackClient | null = null;

/**
 * Lazily initializes and caches the Filestack client.
 *
 * @returns The initialized Filestack client
 */
export async function getFilestackClient(): Promise<FilestackClient> {
  if (!cachedFilestackClient) {
    const filestackJs = await import(/* webpackChunkName: "filestack-js" */ 'filestack-js');
    cachedFilestackClient = filestackJs.init(FILESTACK_API_KEY);
  }
  return cachedFilestackClient;
}

/**
 * Resets the cached Filestack client.
 * Used for testing purposes.
 */
export function resetFilestackClient(): void {
  cachedFilestackClient = null;
}

/**
 * Target dimension for image transformation.
 */
export interface ITransformDimension {
  width: number;
  height: number;
}

/**
 * Creates a transformed (resized) image using Filestack transformations,
 * then uploads it to S3.
 *
 * @param client - The Filestack client
 * @param handle - The Filestack handle for the original image
 * @param targetDimension - The target dimensions to resize to
 * @returns The S3 URL of the transformed image
 */
export async function createTransformedImage(
  client: FilestackClient,
  handle: string,
  targetDimension: ITransformDimension,
): Promise<string> {
  // Use Filestack to create the transformation
  const filestackUrl = client.transform(handle, {
    // Deliberately added to improve quality in #5612
    compress: false,
    quality: { value: 100 },
    resize: {
      fit: EFitOptions.crop,
      height: targetDimension.height,
      width: targetDimension.width,
    },
  });

  // Download the transformed image from Filestack
  const response = await fetch(filestackUrl);
  const transformedBlob = await response.blob();

  // Create a file from the transformed blob
  const transformedFile = new File(
    [transformedBlob],
    `transformed_${targetDimension.width}x${targetDimension.height}.jpg`,
    { type: 'image/jpeg' },
  );

  // Upload the transformed image to S3
  const uploadResult = await client.upload(transformedFile);

  // Construct the S3 URL
  const s3Url = `https://s3.amazonaws.com/${uploadResult.container}/${encodeURIComponent(uploadResult.key)}`;
  return s3Url;
}

/**
 * The default ad tag HTML template for new ad tag creatives.
 */
export const DEFAULT_AD_TAG_HTML = `<ins style="display:inline-block;width:300px;height:250px;">
<a href="@FEATHR_CLICK@" target="_blank">
  <img alt="" src="${noImg300x250}" />
</a>
</ins>
@FEATHR_TRACK@`;

/**
 * Determines if a creative is a video creative.
 * @param creative - The Creative model
 * @returns True if the creative is a video type (DisplayVideo or FacebookVideo)
 */
export function isVideoCreative(creative: Creative): boolean {
  const cls = creative.get('_cls');
  return cls === CreativeClass.DisplayVideo || cls === CreativeClass.FacebookVideo;
}

/**
 * Determines if a creative is a Meta (Facebook) creative.
 * @param creative - The Creative model
 * @returns True if the creative is a Meta type (FacebookImage or FacebookVideo)
 */
export function isMetaCreative(creative: Creative): boolean {
  const cls = creative.get('_cls');
  return cls === CreativeClass.FacebookImage || cls === CreativeClass.FacebookVideo;
}

/**
 * Gets the dimensions string for a creative.
 * @param creative - The Creative model
 * @returns Dimensions string in format "widthxheight" or empty string if invalid
 */
export function getCreativeDimensions(creative: Creative): string {
  const width = creative.get('width', 0);
  const height = creative.get('height', 0);
  return width > 0 && height > 0 ? `${width}x${height}` : '';
}

/**
 * Gets the aspect ratio notation for a creative.
 * @param creative - The Creative model
 * @returns Aspect ratio string (e.g., "16:9") or empty string if invalid
 */
export function getCreativeAspectRatio(creative: Creative): string {
  const width = creative.get('width', 0);
  const height = creative.get('height', 0);
  if (width > 0 && height > 0) {
    return getAspectRatioNotation(width / height);
  }
  return '';
}

/**
 * Gets the URL hash for the creatives step in the campaign edit wizard.
 * @param campaign - The campaign model
 * @returns Hash string for the creatives step ("#step3" or "#step4")
 */
export function getCreativesStepHash(campaign: BaseCampaign): string {
  const { isFacebook, isOmniReachCampaign, isGeofencingCampaign } = campaign;
  const hideFiltersStep = isFacebook || isGeofencingCampaign || isOmniReachCampaign;
  return hideFiltersStep ? '#step3' : '#step4';
}

/**
 * Returns the allowed creative classes for a channel.
 * @param channel - The channel type ('display' or 'meta')
 * @returns Array of CreativeClass values
 */
export function getCreativeClassesForChannel(channel: TChannel): CreativeClass[] {
  if (channel === 'meta') {
    return [CreativeClass.FacebookImage, CreativeClass.FacebookVideo];
  }
  return [
    CreativeClass.DisplayImage,
    CreativeClass.DisplayVideo,
    CreativeClass.DisplayAdTag,
    CreativeClass.DisplayBannersnack,
  ];
}

/**
 * Creative info formatted for display in CreativeListItem.
 */
export interface ICreativeInfo {
  /** Spec name (e.g., "Medium Rectangle", "Square") */
  specName: string;
  /** Dimensions string (e.g., "300x250") */
  dimensions: string;
  /** Aspect ratio string (e.g., "6:5") */
  aspectRatio: string;
}

/**
 * Data structure for displaying a creative in list/card components.
 */
export interface ICreativeListItemData {
  /** Creative ID */
  id: string;
  /** URL for the creative thumbnail */
  thumbnailUrl: string;
  /** Creative name/title */
  title: string;
  /** Spec name (e.g., "Medium Rectangle", "Square") */
  specName: string;
  /** Dimensions string (e.g., "300x250") */
  dimensions: string;
  /** Aspect ratio string (e.g., "6:5") */
  aspectRatio: string;
  /** Whether the creative is a video */
  isVideo: boolean;
}

/**
 * Filters creatives by name using case-insensitive search.
 * @param creatives - Array of DisplayCreative models
 * @param query - Search query string
 * @returns Filtered array of creatives matching the query
 */
export function filterCreativesBySearch(
  creatives: DisplayCreative[],
  query: string,
): DisplayCreative[] {
  if (!query.trim()) {
    return creatives;
  }
  const lowerQuery = query.toLowerCase();
  return creatives.filter((creative) => {
    const name = creative.get('name') ?? '';
    return name.toLowerCase().includes(lowerQuery);
  });
}

/**
 * Gets the size name for creative dimensions by matching against valid dimension sets.
 * For Facebook creatives, matches by aspect ratio with tolerance.
 * @param width - Creative width in pixels
 * @param height - Creative height in pixels
 * @param cls - The creative class
 * @returns The size name (e.g., "Medium Rectangle", "Facebook Standard Square") or "Custom" if not found
 */
function getSizeName(width: number, height: number, cls?: CreativeClass): string {
  const key = `${width}x${height}`;

  if (cls === CreativeClass.FacebookImage) {
    const exactMatch = ValidFacebookDimensions[key];
    if (exactMatch) {
      return exactMatch.name;
    }
    const actualAspectRatio = width / height;
    const match = Object.values(ValidFacebookDimensions).find((dim) => {
      const specAspectRatio = dim.width / dim.height;
      return (
        metaAspectRatiosMatch(actualAspectRatio, specAspectRatio) &&
        width >= dim.width &&
        height >= dim.height
      );
    });
    return match?.name ?? 'Custom';
  }

  if (cls === CreativeClass.FacebookVideo) {
    const exactMatch = ValidFacebookVideoDimensions[key];
    if (exactMatch) {
      return exactMatch.name;
    }
    const actualAspectRatio = width / height;
    const match = Object.values(ValidFacebookVideoDimensions).find((dim) => {
      const specAspectRatio = dim.width / dim.height;
      return (
        metaAspectRatiosMatch(actualAspectRatio, specAspectRatio) &&
        width >= dim.width &&
        height >= dim.height
      );
    });
    return match?.name ?? 'Custom';
  }

  const dimension = ValidDimensions[key];
  return dimension?.name ?? 'Custom';
}

// =============================================================================
// Meta Creative Dimension Validation
// =============================================================================

/**
 * Result of validating a creative's dimensions against Meta's requirements.
 */
export interface IMetaValidationResult {
  /** Whether the creative passes all validation checks */
  isValid: boolean;
  /** Whether the aspect ratio matches a valid Meta spec */
  isAspectRatioValid: boolean;
  /** Whether the dimensions meet the minimum size requirements */
  meetsMinDimensions: boolean;
  /** Minimum width required for this aspect ratio */
  minWidth: number;
  /** Minimum height required for this aspect ratio */
  minHeight: number;
  /** The spec key that best matches these dimensions */
  matchingSpec: string;
}

/**
 * Finds the best matching spec for the given dimensions and aspect ratio.
 * Prefers exact match, then falls back to smallest valid spec.
 *
 * @param matchingDimensions - Array of dimensions that match the aspect ratio
 * @param width - The creative's width
 * @param height - The creative's height
 * @returns The spec key of the best matching dimension
 */
export function findMatchingSpec(
  matchingDimensions: IDimensionDefinition[],
  width: number,
  height: number,
): string {
  const exactMatch = matchingDimensions.find((dim) => dim.width === width && dim.height === height);

  if (exactMatch) {
    return exactMatch.spec;
  }

  const sortedBySize = [...matchingDimensions].sort(
    (a, b) => a.width * a.height - b.width * b.height,
  );

  const bestMatch = sortedBySize.find((dim) => dim.width >= width && dim.height >= height);
  return (bestMatch || sortedBySize[0]).spec;
}

/**
 * Validates creative dimensions against Meta's requirements.
 * Checks aspect ratio (with tolerance) and minimum dimensions.
 *
 * @param width - The creative's width in pixels
 * @param height - The creative's height in pixels
 * @param validDimensions - Record of valid dimension definitions for this creative type
 * @returns Validation result with details about what passed/failed
 */
export function validateMetaCreativeDimensions(
  width: number,
  height: number,
  validDimensions: Record<string, IDimensionDefinition>,
): IMetaValidationResult {
  const creativeAspectRatio = width / height;
  const validAspectRatioValues = Object.values(validDimensions).map(
    (dim) => dim.width / dim.height,
  );

  const isAspectRatioValid = validAspectRatioValues.some((validRatio) =>
    metaAspectRatiosMatch(creativeAspectRatio, validRatio),
  );

  if (!isAspectRatioValid) {
    return {
      isValid: false,
      isAspectRatioValid: false,
      meetsMinDimensions: false,
      minWidth: 0,
      minHeight: 0,
      matchingSpec: '',
    };
  }

  const matchingDimensions = Object.values(validDimensions).filter((dim) =>
    metaAspectRatiosMatch(dim.width / dim.height, creativeAspectRatio),
  );

  const minWidth = Math.min(...matchingDimensions.map((dim) => dim.width));
  const minHeight = Math.min(...matchingDimensions.map((dim) => dim.height));
  const meetsMinDimensions = width >= minWidth && height >= minHeight;
  const matchingSpec = findMatchingSpec(matchingDimensions, width, height);

  return {
    isValid: meetsMinDimensions,
    isAspectRatioValid: true,
    meetsMinDimensions,
    minWidth,
    minHeight,
    matchingSpec,
  };
}

/**
 * Finds the closest valid aspect ratio for a creative that failed validation.
 * Used to provide helpful error messages to users.
 *
 * @param width - The creative's width
 * @param height - The creative's height
 * @param validDimensions - Record of valid dimension definitions
 * @returns A string describing the closest valid aspect ratio (e.g., "1:1")
 */
export function findClosestValidAspectRatio(
  width: number,
  height: number,
  validDimensions: Record<string, IDimensionDefinition>,
): string {
  const creativeRatio = width / height;
  let closestDiff = Infinity;
  let closestDimension: IDimensionDefinition | undefined;

  for (const dimension of Object.values(validDimensions)) {
    const dimRatio = dimension.width / dimension.height;
    const diff = Math.abs(creativeRatio - dimRatio);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestDimension = dimension;
    }
  }

  if (!closestDimension) {
    return '1:1';
  }

  return getAspectRatioNotation(closestDimension.width / closestDimension.height);
}

// =============================================================================
// Display Creative Dimension Validation
// =============================================================================

/**
 * Result of validating a creative's dimensions against Display's requirements.
 */
export interface IDisplayValidationResult {
  /** Whether the creative passes validation */
  isValid: boolean;
  /** The spec key that matches these dimensions (empty if invalid) */
  matchingSpec: string;
  /** Target dimension to resize to (when aspect ratio matches but dimensions differ) */
  targetDimension?: IDimensionDefinition;
  /** The closest valid dimension definition for helpful error messages */
  closestDimension?: IDimensionDefinition;
}

/**
 * Finds the closest valid dimension by comparing aspect ratio difference.
 * Used to provide helpful error messages when aspect ratio doesn't match.
 *
 * @param width - The creative's width in pixels
 * @param height - The creative's height in pixels
 * @param isVideo - Whether this is a video creative
 * @returns The closest matching dimension definition
 */
export function findClosestDisplayDimension(
  width: number,
  height: number,
  isVideo: boolean,
): IDimensionDefinition | undefined {
  const aspectRatio = width / height;
  let closestDiff = Infinity;
  let closestDimension: IDimensionDefinition | undefined;

  for (const dimension of Object.values(ValidDimensions)) {
    // Skip video-only specs for images and vice versa
    const isVideoDimension = dimension.spec.includes('video') || dimension.spec === 'video';
    if (isVideo !== isVideoDimension) {
      continue;
    }

    // Calculate aspect ratio difference
    const dimensionRatio = dimension.width / dimension.height;
    const diff = Math.abs(aspectRatio - dimensionRatio);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestDimension = dimension;
    }
  }

  return closestDimension;
}

/**
 * Finds all valid dimensions that match the given aspect ratio and are smaller
 * than or equal to the original dimensions.
 *
 * @param width - The creative's width in pixels
 * @param height - The creative's height in pixels
 * @param isVideo - Whether this is a video creative
 * @returns Array of matching dimension definitions
 */
export function findMatchingDisplayDimensions(
  width: number,
  height: number,
  isVideo: boolean,
): IDimensionDefinition[] {
  const aspectRatio = width / height;
  const results: IDimensionDefinition[] = [];

  for (const dimension of Object.values(ValidDimensions)) {
    // Skip video-only specs for images and vice versa
    const isVideoDimension = dimension.spec.includes('video') || dimension.spec === 'video';
    if (isVideo !== isVideoDimension) {
      continue;
    }

    const dimensionRatio = dimension.width / dimension.height;

    // Check if aspect ratio matches exactly (no tolerance for display ads)
    const ratioMatches = aspectRatio === dimensionRatio;

    // Include dimensions that are smaller or equal to the original
    const isSmallerOrEqual = dimension.width <= width && dimension.height <= height;

    if (ratioMatches && isSmallerOrEqual) {
      results.push(dimension);
    }
  }

  return results;
}

/**
 * Finds the largest valid dimension that matches the given aspect ratio.
 *
 * @param width - The creative's width in pixels
 * @param height - The creative's height in pixels
 * @param isVideo - Whether this is a video creative
 * @returns The largest matching dimension, or undefined if none match
 */
export function findLargestMatchingDisplayDimension(
  width: number,
  height: number,
  isVideo: boolean,
): IDimensionDefinition | undefined {
  const matchingDimensions = findMatchingDisplayDimensions(width, height, isVideo);

  if (matchingDimensions.length === 0) {
    return undefined;
  }

  // Sort by total pixels (area) descending and return the largest
  return [...matchingDimensions].sort((a, b) => b.width * b.height - a.width * a.height)[0];
}

/**
 * Validates creative dimensions against Display advertising requirements.
 * Uses aspect ratio matching with tolerance, finding the largest valid dimension
 * that the uploaded image can be resized to.
 *
 * @param width - The creative's width in pixels
 * @param height - The creative's height in pixels
 * @param isVideo - Whether this is a video creative
 * @returns Validation result with target dimension for resizing
 *
 * @example
 * ```typescript
 * // Exact match
 * const result = validateDisplayCreativeDimensions(300, 250, false);
 * // result.isValid === true, result.matchingSpec === 'medium_rectangle'
 *
 * // 2x size that matches aspect ratio - will be resized
 * const result2x = validateDisplayCreativeDimensions(600, 500, false);
 * // result2x.isValid === true, result2x.targetDimension.width === 300
 *
 * // Invalid aspect ratio
 * const invalidResult = validateDisplayCreativeDimensions(500, 400, false);
 * // invalidResult.isValid === false, invalidResult.closestDimension provided
 * ```
 */
export function validateDisplayCreativeDimensions(
  width: number,
  height: number,
  isVideo: boolean,
): IDisplayValidationResult {
  // First check for exact match
  const key = `${width}x${height}`;
  const exactMatch = ValidDimensions[key];

  if (exactMatch) {
    // For videos, ensure the matched dimension is a video spec
    const isVideoDimension = exactMatch.spec.includes('video') || exactMatch.spec === 'video';
    if (isVideo && !isVideoDimension) {
      // Video uploaded with image dimensions - find largest matching video dimension
      const largestMatch = findLargestMatchingDisplayDimension(width, height, true);
      if (largestMatch) {
        return {
          isValid: true,
          matchingSpec: largestMatch.spec,
          targetDimension: largestMatch,
        };
      }
      return {
        isValid: false,
        matchingSpec: '',
        closestDimension: findClosestDisplayDimension(width, height, true),
      };
    }

    return {
      isValid: true,
      matchingSpec: exactMatch.spec,
    };
  }

  // No exact match - find the largest dimension that matches the aspect ratio
  const largestMatch = findLargestMatchingDisplayDimension(width, height, isVideo);

  if (largestMatch) {
    return {
      isValid: true,
      matchingSpec: largestMatch.spec,
      targetDimension: largestMatch,
    };
  }

  // No matching aspect ratio - find closest for helpful error message
  return {
    isValid: false,
    matchingSpec: '',
    closestDimension: findClosestDisplayDimension(width, height, isVideo),
  };
}

/**
 * Checks if a creative has valid dimensions.
 * For display creatives, checks exact match against ValidDimensions.
 * For Facebook creatives, checks aspect ratio with tolerance and minimum size.
 * @param creative - The DisplayCreative model
 * @returns True if the creative has valid dimensions
 */
export function hasValidDimensions(creative: DisplayCreative): boolean {
  const width = creative.get('width');
  const height = creative.get('height');
  if (!width || !height) {
    return false;
  }

  const cls = creative.get('_cls');

  if (cls === CreativeClass.FacebookImage) {
    return validateMetaCreativeDimensions(width, height, ValidFacebookDimensions).isValid;
  }

  if (cls === CreativeClass.FacebookVideo) {
    return validateMetaCreativeDimensions(width, height, ValidFacebookVideoDimensions).isValid;
  }

  const key = `${width}x${height}`;
  return key in ValidDimensions;
}

/**
 * Formats creative info for display in CreativeListItem.
 * @param creative - The DisplayCreative model
 * @returns Object with specName, dimensions, and aspectRatio
 */
export function formatCreativeInfo(creative: DisplayCreative): ICreativeInfo {
  const width = creative.get('width') ?? 0;
  const height = creative.get('height') ?? 0;
  const cls = creative.get('_cls');

  return {
    specName: getSizeName(width, height, cls),
    dimensions: creative.dimensions,
    aspectRatio: creative.aspectRatio,
  };
}

/**
 * Extracts a preview image URL from Bannersnack banner_urls.
 * Prefers jpg, then png, then any completed URL.
 *
 * @param bannerUrls - Array of banner URL objects from the creative
 * @returns The best available preview URL, or empty string if none
 */
export function getBannersnackPreviewUrl(bannerUrls: IBannersnackUrl[] | undefined): string {
  if (!bannerUrls?.length) {
    return '';
  }

  const doneUrls = bannerUrls.filter((u) => u.status === 'done');
  const jpg = doneUrls.find((u) => u.type === 'jpg');
  if (jpg) {
    return jpg.url;
  }

  const png = doneUrls.find((u) => u.type === 'png');
  if (png) {
    return png.url;
  }

  return doneUrls[0]?.url ?? '';
}

/**
 * Gets the thumbnail URL for any creative, handling Bannersnack creatives
 * which store their image in banner_urls instead of the url field.
 *
 * @param creative - The DisplayCreative model
 * @returns The best available thumbnail URL
 */
export function getCreativeThumbnailUrl(creative: DisplayCreative): string {
  if (isBannersnackCreative(creative)) {
    return getBannersnackPreviewUrl(creative.get('banner_urls'));
  }

  return creative.get('url') ?? '';
}

/**
 * Waits for a BannersnackEditor unmount sync to complete.
 * The editor's cleanup effect calls model.sync() (fire-and-forget),
 * setting isUpdating to true then false. We use MobX `when` to
 * reactively wait for each transition instead of polling.
 * Timeout is non-fatal — proceeds with whatever state is available.
 *
 * @param creative - The creative model being synced
 */
export async function waitForBannersnackSync(creative: DisplayCreative): Promise<void> {
  try {
    await when(() => creative.isUpdating, { timeout: 10000 });
    await when(() => !creative.isUpdating, { timeout: 30000 });
  } catch {
    // Timeout — proceed with whatever state we have
  }
}

/**
 * Syncs a Bannersnack creative and validates its dimensions.
 * Shows an error toast if dimensions are invalid.
 *
 * @param creative - The creative to sync and validate
 * @param t - Translation function for error messages
 * @returns True if dimensions are valid, false otherwise
 */
export async function syncAndValidateBannersnackCreative(
  creative: DisplayCreative,
  t: TFunction,
): Promise<boolean> {
  await waitForBannersnackSync(creative);

  const dimensionKey = `${creative.get('width')}x${creative.get('height')}`;
  if (dimensionKey in ValidDimensions) {
    return true;
  }

  const validSizes = Object.keys(ValidDimensions).join(', ');
  toast(
    t('Invalid dimensions ({{dimensions}}). Please resize to a valid size: {{validSizes}}', {
      dimensions: dimensionKey,
      validSizes,
    }),
    { type: 'error' },
  );
  return false;
}

/**
 * Converts a DisplayCreative model to display data for CreativeListItem.
 * @param creative - The DisplayCreative model
 * @returns Object with all display properties needed for list items
 */
export function creativeToDisplayData(creative: DisplayCreative): ICreativeListItemData {
  const info = formatCreativeInfo(creative);
  return {
    id: creative.id,
    thumbnailUrl: getCreativeThumbnailUrl(creative),
    title: creative.get('name') ?? '',
    specName: info.specName,
    dimensions: info.dimensions,
    aspectRatio: info.aspectRatio,
    isVideo: isVideoCreative(creative),
  };
}

// =============================================================================
// Creative Status Utilities
// =============================================================================

/**
 * Result of determining a creative's status for display.
 */
export interface ICreativeStatusResult {
  /** The visual status indicator: 'ready', 'warning', or 'error' */
  status: TCreativeStatus;
  /** Tooltip text describing the status */
  tooltip: string;
}

/**
 * Maps display creative audit status to visual status indicator.
 *
 * @param statusValue - The audit status value (e.g., 'approved', 'rejected')
 * @returns The visual status: 'ready', 'warning', or 'error'
 */
function mapDisplayAuditStatusToVisual(
  statusValue: TCreativeAuditStatus | undefined,
): TCreativeStatus {
  switch (statusValue) {
    case 'rejected':
      return 'error';

    case 'unauditable':
      return 'warning';

    default:
      return 'ready';
  }
}

/**
 * Gets the display label for an audit status.
 *
 * @param statusValue - The audit status value
 * @param t - Translation function
 * @returns Translated status label
 */
function getDisplayAuditStatusLabel(
  statusValue: TCreativeAuditStatus | undefined,
  t: TFunction,
): string {
  switch (statusValue) {
    case 'not audited':
      return t('Not reviewed');

    case 'pending':
      return t('Pending review');

    case 'approved':
      return t('Approved');

    case 'rejected':
      return t('Rejected');

    case 'unauditable':
      return t('Not reviewable');

    default:
      return t('Not reviewed');
  }
}

/**
 * Builds tooltip text from display creative audit statuses.
 *
 * @param statuses - Array of audit statuses from different auditors
 * @param uniqueStatuses - Pre-computed unique status values
 * @param t - Translation function
 * @returns Formatted tooltip string
 */
function buildDisplayStatusTooltip(
  statuses: ICreativeAuditStatus[],
  uniqueStatuses: TCreativeAuditStatus[],
  t: TFunction,
): string {
  if (uniqueStatuses.length === 0) {
    return t('Not reviewed');
  }

  if (uniqueStatuses.length > 1) {
    return t('Mixed');
  }

  const primaryStatus = uniqueStatuses[0];
  const label = getDisplayAuditStatusLabel(primaryStatus, t);

  if (primaryStatus === 'rejected') {
    const reasons = statuses
      .filter((s) => s.status === 'rejected' && s.reason)
      .map((s) => s.reason);
    if (reasons.length > 0) {
      return `${label}: ${reasons.join(', ')}`;
    }
  }

  return label;
}

/**
 * Gets the creative status for display (TTD) creatives.
 *
 * Uses the `audit_statuses` array to determine status:
 * - Empty or 'not audited' -> ready, "Not reviewed"
 * - 'pending' -> ready, "Pending review"
 * - 'approved' -> ready, "Approved"
 * - 'rejected' -> error, "Rejected: [reason]"
 * - 'unauditable' -> warning, "Not reviewable"
 * - Mixed statuses -> warning, "Mixed"
 *
 * @param creative - The display creative to check
 * @param t - Translation function
 * @returns Status and tooltip for the creative
 */
export function getDisplayCreativeStatus(
  creative: DisplayCreative,
  t: TFunction,
): ICreativeStatusResult {
  const statuses = (creative.get('audit_statuses') as ICreativeAuditStatus[] | undefined) ?? [];
  const uniqueStatuses = [...new Set(statuses.map((s) => s.status))].filter(
    (s): s is TCreativeAuditStatus => Boolean(s),
  );

  let visualStatus: TCreativeStatus;
  if (uniqueStatuses.length > 1) {
    visualStatus = 'warning';
  } else if (uniqueStatuses.length === 0) {
    visualStatus = 'ready';
  } else {
    visualStatus = mapDisplayAuditStatusToVisual(uniqueStatuses[0]);
  }

  return {
    status: visualStatus,
    tooltip: buildDisplayStatusTooltip(statuses, uniqueStatuses, t),
  };
}

/**
 * Maps Meta ad_status to visual status indicator.
 *
 * @param adStatus - The Meta ad status value
 * @returns The visual status: 'ready', 'warning', or 'error'
 */
function mapMetaAdStatusToVisual(adStatus: string | undefined): TCreativeStatus {
  switch (adStatus) {
    case 'DISAPPROVED':

    case 'ARCHIVED':
      return 'error';

    case 'ACTIVE':
      return 'ready';

    case 'PAUSED':

    case 'WITH_ISSUES':

    case 'PENDING_REVIEW':

    case 'IN_PROCESS':

    default:
      return 'warning';
  }
}

/**
 * Gets the display label for a Meta ad status.
 *
 * @param adStatus - The Meta ad status value
 * @param t - Translation function
 * @returns Translated status label
 */
function getMetaAdStatusLabel(adStatus: string | undefined, t: TFunction): string {
  switch (adStatus) {
    case 'ACTIVE':
      return t('Approved');

    case 'PENDING_REVIEW':

    case 'IN_PROCESS':
      return t('Pending review');

    case 'DISAPPROVED':
      return t('Rejected');

    case 'WITH_ISSUES':
      return t('Has issues');

    case 'PAUSED':
      return t('Paused');

    case 'ARCHIVED':
      return t('Archived');

    default:
      return t('Pending review');
  }
}

/**
 * Extracts all feedback text from Meta ad review feedback object.
 * Collects feedback from facebook, instagram, and general feedback sources,
 * deduplicates them, and returns a combined string.
 *
 * @param adReviewFeedback - The feedback object from Meta
 * @returns All unique feedback messages joined by "; ", or undefined if none
 */
function extractMetaFeedback(
  adReviewFeedback:
    | {
        facebook?: Record<string, string>;
        instagram?: Record<string, string>;
        feedback?: Record<string, string>;
      }
    | undefined,
): string | undefined {
  if (!adReviewFeedback) {
    return undefined;
  }

  const allFeedback: string[] = [];

  // Collect feedback from all sources
  const { facebook, instagram, feedback } = adReviewFeedback;

  if (facebook) {
    allFeedback.push(...Object.values(facebook).filter(Boolean));
  }

  if (instagram) {
    allFeedback.push(...Object.values(instagram).filter(Boolean));
  }

  if (feedback) {
    allFeedback.push(...Object.values(feedback).filter(Boolean));
  }

  // Deduplicate feedback messages
  const uniqueFeedback = [...new Set(allFeedback)];

  if (uniqueFeedback.length === 0) {
    return undefined;
  }

  return uniqueFeedback.join('; ');
}

/**
 * Gets the creative status for Meta (Facebook/Instagram) creatives.
 *
 * Uses the `ad_status` field to determine status:
 * - ACTIVE -> ready, "Approved"
 * - PENDING_REVIEW / IN_PROCESS -> warning, "Pending review"
 * - PAUSED -> warning, "Paused"
 * - WITH_ISSUES -> warning, "Has issues: [feedback]"
 * - DISAPPROVED -> error, "Rejected: [feedback]"
 * - ARCHIVED -> error, "Archived"
 * - undefined -> warning, "Pending review"
 *
 * @param creative - The Meta creative to check
 * @param t - Translation function
 * @returns Status and tooltip for the creative
 */
export function getMetaCreativeStatus(
  creative: FacebookDisplayCreative,
  t: TFunction,
): ICreativeStatusResult {
  const adStatus = creative.get('ad_status');
  const adReviewFeedback = creative.get('ad_review_feedback');

  const visualStatus = mapMetaAdStatusToVisual(adStatus);
  const label = getMetaAdStatusLabel(adStatus, t);

  let tooltip = label;
  if ((adStatus === 'DISAPPROVED' || adStatus === 'WITH_ISSUES') && adReviewFeedback) {
    const feedback = extractMetaFeedback(adReviewFeedback);
    if (feedback) {
      tooltip = `${label}: ${feedback}`;
    }
  }

  return {
    status: visualStatus,
    tooltip,
  };
}

/**
 * Gets the creative status for any creative type.
 *
 * Automatically detects whether the creative is a Meta or Display creative
 * and delegates to the appropriate status function.
 *
 * @param creative - The creative to check
 * @param t - Translation function
 * @returns Status and tooltip for the creative
 */
export function getCreativeStatus(
  creative: DisplayCreative | FacebookDisplayCreative,
  t: TFunction,
): ICreativeStatusResult {
  if (creative.isFacebook) {
    return getMetaCreativeStatus(creative as FacebookDisplayCreative, t);
  }
  return getDisplayCreativeStatus(creative, t);
}
